import { CalendricalType, IRecurringWorkflowSettings } from '~/client/graph'

import ProjectDateStore, { isAfter } from '../stores/ui/ProjectDate.store'
import IMsDateInterval from '../types/IMsDateInterval'
import BaseModel from './BaseModel'

const allowedRecurringFrequencies = [CalendricalType.Day, CalendricalType.Week]
export default class RecurringWorkflowSettings
  extends BaseModel<IRecurringWorkflowSettings>
  implements IRecurringWorkflowSettings
{
  public static fromDto(
    dto: IRecurringWorkflowSettings,
  ): RecurringWorkflowSettings {
    return new RecurringWorkflowSettings(
      dto.id,
      dto.projectId,
      dto.frequency,
      dto.frequencyType,
      dto.endDate,
      dto.selectedDays,
      dto.createdAt,
      dto.updatedAt,
    )
  }

  public static createNewWeeklySetting(
    projectId: string,
    projectDateStore: ProjectDateStore,
    selectedDays: number[],
    endDate: Date | number = new Date(),
  ): RecurringWorkflowSettings {
    const { endOfDay } = projectDateStore

    return new RecurringWorkflowSettings(
      null,
      projectId,
      1,
      CalendricalType.Week,
      endOfDay(endDate).getTime(),
      selectedDays,
    )
  }

  public constructor(
    public id: string,
    public projectId: string,
    public frequency: number,
    public frequencyType: CalendricalType,
    public endDate: number,
    public selectedDays?: number[],
    public createdAt: number = 0,
    public updatedAt: number = 0,
  ) {
    super(id)
    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)
  }

  public getRecurringDates(
    startDate: number,
    endDate: number,
    projectDateStore: ProjectDateStore,
  ): IMsDateInterval[] {
    const {
      endDate: endRepeatDate,
      frequencyType,
      frequency,
      selectedDays,
    } = this
    const {
      addDays,
      addWeeks,
      isSameWeek,
      getWeekdayNumber,
      isInsideWorkingDaysAndHours,
    } = projectDateStore

    const dates: IMsDateInterval[] = []

    const isDailyFrequency = frequencyType === CalendricalType.Day
    const isWeeklyFrequency = frequencyType === CalendricalType.Week

    while (
      !isAfter(endDate, endRepeatDate) ||
      (isWeeklyFrequency && isSameWeek(startDate, endRepeatDate))
    ) {
      if (!allowedRecurringFrequencies.includes(frequencyType)) {
        break
      }

      if (isDailyFrequency) {
        dates.push({ startDate, endDate })

        for (let freq = 0; freq < frequency;) {
          startDate = addDays(startDate, 1).getTime()
          endDate = addDays(endDate, 1).getTime()

          if (isInsideWorkingDaysAndHours(startDate, endDate)) {
            freq++
          }
        }
        continue
      }

      if (!selectedDays?.length) {
        break
      }

      for (const dayOfWeek of selectedDays) {
        const startDayOfWeek = +getWeekdayNumber(startDate)
        const endDateOfWeek = +getWeekdayNumber(endDate)

        const dayStart = addDays(
          startDate,
          dayOfWeek - startDayOfWeek,
        ).getTime()
        const dayEnd = addDays(endDate, dayOfWeek - endDateOfWeek).getTime()

        if (
          dayStart < startDate ||
          dayEnd > endRepeatDate ||
          !isInsideWorkingDaysAndHours(startDate, endDate)
        ) {
          continue
        }

        dates.push({ startDate: dayStart, endDate: dayEnd })
      }

      startDate = addWeeks(startDate, frequency).getTime()
      endDate = addWeeks(endDate, frequency).getTime()
    }

    return dates
  }
}
